import React from "react";
import ReactEChartsCore from "echarts-for-react";
import * as echarts from "echarts/core";
import { CustomEChartsLegend } from "./components/CustomEChartsLegend";
import type { ChartSettings } from "./constants";
import { buildGridConfig } from "./utils/axisUtils";

// Charts
import { BarChart, LineChart, PieChart, ScatterChart } from "echarts/charts";
// Components
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
} from "echarts/components";
// Renderer
import { CanvasRenderer, SVGRenderer } from "echarts/renderers";

// Register the required components once at module load
// (This keeps bundle size low by importing only what we need for MVP.)
// Adapters that target native ECharts legends often reserve ~180px (right) or ~60px (bottom).
const NATIVE_LEGEND_HORIZONTAL_GRID_PADDING = 100;
const NATIVE_LEGEND_BOTTOM_GRID_PADDING = 55;

echarts.use([
  GridComponent,
  TooltipComponent,
  LegendComponent,
  TitleComponent,
  DatasetComponent,
  BarChart,
  LineChart,
  PieChart,
  ScatterChart,
  CanvasRenderer,
  SVGRenderer,
]);

export interface EChartRendererProps {
  /**
   * A fully-formed ECharts option object.
   * See https://echarts.apache.org/en/api.html#echarts for reference.
   */
  option: echarts.EChartsCoreOption;
  /** Optional theme name registered via echarts.registerTheme */
  theme?: "light" | "dark";
  /** Inline styles passed to the underlying container */
  style?: React.CSSProperties;
  /** Optional additional className */
  className?: string;
  /** Chart settings for custom legend */
  settings?: Partial<ChartSettings>;
  /** Container width for legend calculations */
  containerWidth?: number;
  /** Renderer mode: 'canvas' or 'svg'. Defaults to 'canvas' */
  renderer?: "canvas" | "svg";
  /** Preloaded chart colors to pass to legend (prevents race condition in PDF) */
  preloadedChartColors?: string[];
  /** Report builder chart type — forwarded to {@link CustomEChartsLegend} (e.g. cash flow legend parity). */
  chartType?: string;
  /** Callback when chart finishes rendering (safe for PDF capture) */
  onFinished?: () => void;
}

export const EChartRenderer: React.FC<EChartRendererProps> = ({
  option,
  theme = "light",
  style,
  className = "",
  settings,
  containerWidth,
  renderer = "canvas",
  preloadedChartColors,
  chartType,
  onFinished,
}) => {
  const chartRef = React.useRef<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Resize using current instance from ref (never capture instance in closure to avoid "disposed" warnings)
  const safeResize = React.useCallback(() => {
    const current = chartRef.current?.getEchartsInstance?.();
    if (current) {
      try {
        current.resize();
      } catch {
        // Ignore disposed or other ECharts errors
      }
    }
  }, []);

  // Resize chart when container size changes
  React.useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver(safeResize);
    resizeObserver.observe(containerRef.current);

    // Trigger initial resize after a short delay to ensure chart is mounted
    const initialResizeTimer = setTimeout(safeResize, 50);

    return () => {
      resizeObserver.disconnect();
      clearTimeout(initialResizeTimer);
    };
  }, [safeResize]);

  // Also resize when option changes (chart may have been re-initialized)
  React.useEffect(() => {
    // Get current instance when timer fires, not now, to avoid resizing a disposed instance
    const timer = setTimeout(safeResize, 10);
    return () => clearTimeout(timer);
  }, [option, safeResize]);
  // Check if legend is actually shown - calculate once and reuse
  // Priority: Check the option's legend.show (what was actually generated)
  // Fall back to settings.legend.show if explicitly set
  const legendShown = React.useMemo(() => {
    // Check what the option actually says about legend visibility
    const optionLegend = (option as any).legend;
    const optionLegendShow = Array.isArray(optionLegend)
      ? optionLegend.some((l: any) => l?.show !== false)
      : optionLegend?.show !== false;
    
    // If settings explicitly sets legend.show, that takes precedence
    if (settings?.legend?.show !== undefined) {
      return settings.legend.show;
    }
    
    // Otherwise use what the option says (handles scatter charts with includeLabels)
    return optionLegendShow;
  }, [option, settings?.legend?.show]);

  const legendPosition = settings?.legendPosition || "bottom";

  // Disable native ECharts legend and use custom legend instead
  const modifiedOption = React.useMemo(() => {
    const xAxisRaw = (option as any).xAxis;
    const xAxis = Array.isArray(xAxisRaw) ? xAxisRaw[0] : xAxisRaw;
    const yAxis = (option as any).yAxis;

    // Check for axis titles
    const hasXAxisTitle = xAxis?.name || settings?.xAxisTitle;
    const hasYAxisTitle = yAxis?.name || settings?.showYAxisTitle === true;

    // Use centralized grid config utility
    const originalGrid = (option as any).grid || {};
    const calculatedGrid = buildGridConfig(settings, {
      legendVisible: legendShown,
      hasXAxisTitle: !!hasXAxisTitle,
      hasYAxisTitle: !!hasYAxisTitle,
    });

    // Merge original grid settings with calculated defaults
    // Original grid takes precedence for any explicitly set values
    const userGrid = settings?.grid || {};
    const adjustedGrid = {
      ...calculatedGrid,
      ...originalGrid,
      // User settings from chartConfig.settings.grid take highest precedence
      ...userGrid,
    };

    // CustomEChartsLegend is rendered outside ECharts in flex layout. Adapters may still
    // reserve native-legend grid space — strip oversized margins so the plot fills width.
    if (legendShown) {
      if (
        userGrid.right === undefined &&
        Number(originalGrid.right) >= NATIVE_LEGEND_HORIZONTAL_GRID_PADDING
      ) {
        adjustedGrid.right = calculatedGrid.right;
      }
      if (
        userGrid.left === undefined &&
        Number(originalGrid.left) >= NATIVE_LEGEND_HORIZONTAL_GRID_PADDING
      ) {
        adjustedGrid.left = calculatedGrid.left;
      }
      if (
        userGrid.bottom === undefined &&
        legendPosition !== "bottom" &&
        Number(originalGrid.bottom) >= NATIVE_LEGEND_BOTTOM_GRID_PADDING
      ) {
        adjustedGrid.bottom = calculatedGrid.bottom;
      }
    }

    // Ensure grid lines are visible with theme-appropriate colors
    // Default grid color is light gray (#e2e8f0) which works for light theme
    // For dark theme, use a darker color for visibility
    const defaultGridColor = theme === "dark" ? "#475569" : "#e2e8f0";

    const processAxis = (axis: any, axisSettings?: any) => {
      if (!axis) return axis;
      if (Array.isArray(axis)) {
        return axis.map((a) => processAxis(a, axisSettings));
      }

      // Ensure axisTick is always hidden (PDF doesn't show tick marks)
      // Ensure axisLine is shown if it exists (preserve existing configuration)
      const processedAxis: any = {
        ...axis,
        axisTick: {
          ...axis.axisTick,
          show: false,
        },
      };

      // Preserve axisLine settings from original configuration
      if (axis.axisLine !== undefined) {
        processedAxis.axisLine = {
          ...axis.axisLine,
          // Preserve show setting exactly as configured (don't default to true)
          show: axis.axisLine.show,
          // Preserve onZero setting from original configuration (set by useReportChart based on negative value detection)
          onZero:
            axis.axisLine.onZero !== undefined ? axis.axisLine.onZero : false,
          lineStyle: {
            ...axis.axisLine.lineStyle,
            color: axis.axisLine.lineStyle?.color || "#94A3B8",
          },
        };
      } else {
        // Add axisLine if missing (for dummy data paths that don't configure it)
        processedAxis.axisLine = {
          show: true,
          onZero: false,
          lineStyle: { color: "#94A3B8" },
        };
      }

      // Ensure splitLine exists and is properly configured
      // Prioritize original option value, fall back to settings.showGrid
      if (axis.splitLine !== undefined) {
        const currentShow = axis.splitLine?.show;
        // If the adapter explicitly set splitLine.show, use that value
        // Only fall back to settings.showGrid if the adapter didn't set it
        const shouldShow =
          currentShow !== undefined
            ? currentShow
            : axisSettings?.showGrid === true;
        const currentColor = axis.splitLine?.lineStyle?.color;
        // Use user-specified gridColor from settings if provided, otherwise use theme-appropriate default
        const userGridColor = axisSettings?.gridColor;
        const gridColor =
          userGridColor ||
          (currentColor && currentColor !== "#e2e8f0"
            ? currentColor
            : defaultGridColor);

        processedAxis.splitLine = {
          ...axis.splitLine,
          show: shouldShow,
          alignWithLabel:
            axis.splitLine?.alignWithLabel !== undefined
              ? axis.splitLine.alignWithLabel
              : true, // Align grid lines with axis labels by default
          lineStyle: {
            ...axis.splitLine?.lineStyle,
            color: gridColor,
            type: axis.splitLine?.lineStyle?.type || "solid",
            opacity: axis.splitLine?.lineStyle?.opacity ?? 0.3,
          },
        };
      }

      return processedAxis;
    };

    const processedXAxis = processAxis((option as any).xAxis, settings);
    const processedYAxis = processAxis((option as any).yAxis, settings);

    // Base option adjustments
    const baseOption: any = {
      ...option,
      backgroundColor: (option as any).backgroundColor || "transparent",
      legend: {
        show: false,
      },
      grid: adjustedGrid,
      xAxis: processedXAxis,
      yAxis: processedYAxis,
    };

    // Disable animations when using SVG renderer (builder/PDF) for deterministic snapshots
    if (renderer === "svg") {
      baseOption.animation = false;
      baseOption.animationDuration = 0;
      baseOption.animationEasing = "linear";
      if (Array.isArray((baseOption as any).series)) {
        (baseOption as any).series = (baseOption as any).series.map((s: any) => ({
          ...s,
          animation: false,
          animationDuration: 0,
          animationEasing: "linear",
        }));
      } else if ((baseOption as any).series) {
        (baseOption as any).series = {
          ...(baseOption as any).series,
          animation: false,
          animationDuration: 0,
          animationEasing: "linear",
        };
      }
    }

    return baseOption;
  }, [option, settings?.xAxisTitle, theme, renderer, legendShown, legendPosition, settings]);
  const isHorizontalLegendLayout = legendPosition === "left" || legendPosition === "right";
  const isLegendFirst = legendPosition === "top" || legendPosition === "left";

  // Calculate legend gap - supports negative values via margins instead of gap
  const legendGapValue = (() => {
    if (!legendShown) return 0;
    if (settings?.legendGap !== undefined) return settings.legendGap;
    return isHorizontalLegendLayout ? 12 : 4;
  })();

  // For positive gaps, use CSS gap; for negative, use margins on legend container
  const useFlexGap = legendGapValue >= 0;

  // Calculate margin style for legend container when using negative gap
  const getLegendMarginStyle = (isFirst: boolean): React.CSSProperties => {
    if (useFlexGap) return {};
    
    // Apply negative margin to bring legend closer to chart
    if (isHorizontalLegendLayout) {
      // Left/right positioned legends
      return isFirst
        ? { marginRight: `${legendGapValue}px` }  // Legend on left, negative margin pulls it right
        : { marginLeft: `${legendGapValue}px` };   // Legend on right, negative margin pulls it left
    } else {
      // Top/bottom positioned legends
      return isFirst
        ? { marginBottom: `${legendGapValue}px` } // Legend on top, negative margin pulls it down
        : { marginTop: `${legendGapValue}px` };    // Legend on bottom, negative margin pulls it up
    }
  };

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: isHorizontalLegendLayout ? "row" : "column",
        background: "transparent",
        gap: useFlexGap ? `${legendGapValue}px` : "0px",
      }}
    >
      {/* Render legend first if position is top or left */}
      {legendShown && isLegendFirst && (
        <div
          style={{
            display: "flex",
            alignItems: isHorizontalLegendLayout ? "center" : "flex-start",
            flexShrink: 0,
            ...(isHorizontalLegendLayout ? { width: "auto", maxWidth: "30%" } : {}),
            ...getLegendMarginStyle(true),
          }}
        >
          <CustomEChartsLegend
            chartOption={option}
            settings={settings}
            containerWidth={containerWidth}
            theme={theme}
            preloadedChartColors={preloadedChartColors}
            chartType={chartType}
          />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, minWidth: 0, background: "transparent" }}>
        <ReactEChartsCore
          ref={chartRef}
          key={renderer} 
          echarts={echarts}
          option={modifiedOption}
          theme={theme}
          notMerge={true}
          lazyUpdate={true}
          opts={{ renderer }}
          style={{
            width: "100%",
            height: "100%",
            background: "transparent",
            ...style,
          }}
          className={className}
          onChartReady={(chart) => {
            // Force resize after chart is ready; get current instance when rAF runs to avoid disposed instance
            requestAnimationFrame(() => {
              requestAnimationFrame(safeResize);
            });

            // Set up finished event listener for PDF capture timing
            if (onFinished) {
              let hasFired = false;
              const fireOnce = () => {
                if (!hasFired) {
                  hasFired = true;
                  onFinished();
                }
              };

              chart.off("finished");
              chart.on("finished", fireOnce);

              // For SVG renderer with no animation, finished may not fire reliably
              // Use rAF as fallback to ensure rendering is complete
              requestAnimationFrame(() => {
                requestAnimationFrame(fireOnce);
              });
            }
          }}
        />
      </div>
      {/* Render legend last if position is bottom or right */}
      {legendShown && !isLegendFirst && (
        <div
          style={{
            display: "flex",
            alignItems: isHorizontalLegendLayout ? "center" : "flex-end",
            flexShrink: 0,
            ...(isHorizontalLegendLayout ? { width: "auto", maxWidth: "30%" } : {}),
            ...getLegendMarginStyle(false),
          }}
        >
          <CustomEChartsLegend
            chartOption={option}
            settings={settings}
            containerWidth={containerWidth}
            theme={theme}
            preloadedChartColors={preloadedChartColors}
            chartType={chartType}
          />
        </div>
      )}
    </div>
  );
};
